import cx from "classnames";
import { LoaderFunction, useLoaderData, useNavigate } from "react-router-dom";

import { get } from "api";
import { Icon } from "atoms/icon/Icon";
import CartCheckIcon from "atoms/icon/icons/ic_added_to_cart_check_ol.svg";
import Memberships from "atoms/icon/icons/memberships_filled.svg";
import { queryClient } from "pages/Root";
import { loadIfFlag } from "services/Flags";
import { waitObj } from "toolbox/Promise";

const loader = async (weightLossSubscriptionId: string | undefined) => {
  const user = await queryClient.fetchQuery(["/user"], () => get("/user"));

  if (!weightLossSubscriptionId)
    return waitObj({ weightLossProgram: null, user });
  const weightLossPrograms = queryClient.fetchQuery(
    ["/healthie/subscriptions"],
    () => get("/healthie/subscriptions")
  );
  const weightLossProgram = (await weightLossPrograms).find(
    (wlp) => wlp.id === weightLossSubscriptionId
  );
  return waitObj({ weightLossProgram, user });
};

type LoaderData = Awaited<ReturnType<typeof loader>>;
export const weightLossProgramCheckoutSuccessLoader: LoaderFunction =
  loadIfFlag("repeatmd-glp")(({ params }) => {
    return loader(params.id);
  });

const WeightLossProgramCheckoutSuccessPage = () => {
  const navigate = useNavigate();
  const { weightLossProgram } = useLoaderData() as LoaderData;

  if (!weightLossProgram) return null;
  const modality = localStorage.getItem("patientModality");
  const navigateTo =
    modality === "sync_visit"
      ? `/health/glp/booking`
      : `/pdp/weight-loss-program/${weightLossProgram.id}`;

  return (
    <div className="mt-[env(safe-area-inset-top)] h-screen flex-col items-center justify-center bg-one text-center">
      <div className="flex items-center justify-center py-12">
        <Icon svg={CartCheckIcon} size="xlarge" color="primaryText" />
      </div>
      <div className="pb-8 font-serif text-h2">
        <div>Get excited,</div>
        <div>your order is confirmed!</div>
      </div>
      <div className="flex h-full flex-col gap-4">
        <div className="px-4">
          <div className="pb-6 text-body1 text-primary">
            Successfully Purchased 1 New Weight Loss Program
          </div>
          <div>
            <div className="divide-y divide-solid divide-background-two overflow-clip rounded-lg shadow-subtle">
              <div
                className="flex cursor-pointer items-center justify-between bg-white py-2 pl-2 pr-4"
                tabIndex={0}
                onClick={() => navigate(navigateTo)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") navigate(navigateTo);
                }}
              >
                <div className="flex">
                  {weightLossProgram.imageUrl && (
                    <div className="mr-3 h-[86px] w-[86px] self-center">
                      <img
                        className={cx(
                          "h-[86px] w-[86px] max-w-none rounded object-cover"
                        )}
                        src={weightLossProgram.imageUrl}
                        height={86}
                        alt="Weight Loss Program"
                      />
                    </div>
                  )}
                  <div
                    className={cx(
                      "my-2 mr-2",
                      !weightLossProgram.imageUrl && "pl-2"
                    )}
                  >
                    <div className={cx("flex gap-1 pb-2")}>
                      <Icon
                        color="brandColor"
                        size="x2small"
                        svg={Memberships}
                      />
                    </div>
                    <div
                      className={cx(
                        "flex flex-col items-start justify-start pb-0.5 pr-4 text-left font-sans text-bold1"
                      )}
                    >
                      {weightLossProgram.name}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeightLossProgramCheckoutSuccessPage;
